import { useQuery } from "@tanstack/react-query";
import {
  Form,
  required,
  useDataProvider,
  useLocaleState,
  useNotify,
  useRedirect,
} from "ra-core";
import { useState } from "react";
import { useFormContext } from "react-hook-form";

import { BooleanInput } from "@/components/admin/boolean-input";
import { CancelButton } from "@/components/admin/cancel-button";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { RequestPreview } from "./RequestPreview";
import { isFakeZinspector } from "../providers/zinspector/client";
import { CONTACT_ROLES } from "../providers/zinspector/crmConfig";
import type { ZcrmDataProvider } from "../providers/zinspector/dataProvider";
import {
  localeSettings,
  withMapping,
} from "../providers/zinspector/i18nProvider";
import {
  ACCOUNT_DETAILS,
  ACCOUNT_KINDS,
  buildNewClientRequests,
  hasContact,
  type NewClientInput,
  type ZiWriteRequest,
} from "../providers/zinspector/newClient";
import {
  contactFields,
  label,
  objects,
  propertyFields,
  type Section,
} from "../providers/zinspector/vocabulary";

const toChoices = (values: readonly string[]) =>
  values.map((value) => ({ id: value, name: value }));

const validateClient = (values: Record<string, unknown>) => {
  const input = values as unknown as NewClientInput;
  const errors: Record<string, string> = {};
  if (hasContact(input) && !input.contact_first_name?.trim()) {
    errors.contact_first_name = "ra.validation.required";
  }
  return errors;
};

/**
 * New account ("client"): an account is a zInspector property in a "CRM – <Market>" group, with
 * its details in zData and its people as contacts (see providers/zinspector/newClient.ts).
 * zInspector's public API cannot create these yet, so against the live API the form shows the
 * exact requests zCRM would send; the demo's fake zInspector runs them.
 */
export const AccountCreate = () => {
  const dataProvider = useDataProvider<ZcrmDataProvider>();
  const [locale] = useLocaleState();
  const { mode, showMapping } = localeSettings(locale);
  const [preview, setPreview] = useState<ZiWriteRequest[] | null>(null);
  const canCreate = isFakeZinspector();

  const { data: markets = [] } = useQuery({
    queryKey: ["zcrm", "markets"],
    queryFn: () => dataProvider.getMarkets(),
  });

  /** A vocabulary label, with its stored zInspector field in a field-mapping locale. */
  const field = (section: Section, stored: string, table: string) =>
    withMapping(
      label(section, stored, mode) ?? stored,
      `${table}.${stored}`,
      showMapping,
    );
  const detailLabel = (detail: string) =>
    withMapping(detail, `zData Account › ${detail}`, showMapping);
  const account = label(objects, "property_account", mode) ?? "Property";
  const contact = label(objects, "contact_tenant", mode) ?? "Contact";

  return (
    <div className="mt-2 flex flex-col gap-4 lg:mr-72">
      <div>
        <h1 className="text-2xl font-semibold">New {account.toLowerCase()}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {canCreate
            ? "Demo: this creates the account in the sample data, through the requests zCRM proposes to zInspector. It resets on reload."
            : "zInspector's public API can't create accounts yet. Fill in the form to see the exact requests zCRM would send; nothing is written."}
        </p>
      </div>
      <Form validate={validateClient} defaultValues={{ country: "US" }}>
        <Card>
          <CardContent className="flex flex-col gap-8">
            <Section title={account}>
              <TextInput
                source="name"
                label={field(propertyFields, "Name", "Property")}
                validate={required()}
              />
              <SelectInput
                source="user_group"
                label={field(propertyFields, "UserGroup", "Property")}
                choices={markets}
                optionValue="value"
                optionText="label"
                validate={required()}
              />
              <TextInput
                source="accounting_id"
                label={field(propertyFields, "AccountingID", "Property")}
              />
            </Section>

            <Section
              title={label(propertyFields, "Address", mode) ?? "Address"}
            >
              <TextInput
                source="street_address"
                label={field(propertyFields, "StreetAddress", "Property")}
              />
              <TextInput
                source="street_address2"
                label={field(propertyFields, "StreetAddress2", "Property")}
              />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <TextInput
                  source="city"
                  label={field(propertyFields, "City", "Property")}
                />
                <TextInput
                  source="state"
                  label={field(propertyFields, "State", "Property")}
                />
                <TextInput
                  source="zip"
                  label={field(propertyFields, "Zip", "Property")}
                />
              </div>
              <TextInput
                source="country"
                label={field(propertyFields, "Country", "Property")}
              />
            </Section>

            <Section
              title={
                label(objects, "zdata_account_area", mode) ?? "Account details"
              }
            >
              {ACCOUNT_DETAILS.map(({ key, detail }) =>
                key === "account_kind" ? (
                  <SelectInput
                    key={key}
                    source={key}
                    label={detailLabel(detail)}
                    choices={toChoices(ACCOUNT_KINDS)}
                  />
                ) : (
                  <TextInput
                    key={key}
                    source={key}
                    label={detailLabel(detail)}
                  />
                ),
              )}
            </Section>

            <Section title={`Main ${contact.toLowerCase()} (optional)`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextInput
                  source="contact_first_name"
                  label={field(contactFields, "FirstName", "Contact")}
                />
                <TextInput
                  source="contact_last_name"
                  label={field(contactFields, "LastName", "Contact")}
                />
              </div>
              <TextInput
                source="contact_email"
                type="email"
                label={field(contactFields, "Email", "Contact")}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextInput
                  source="contact_mobile_phone"
                  label={field(contactFields, "MobilePhone", "Contact")}
                />
                <TextInput
                  source="contact_office_phone"
                  label={field(contactFields, "OfficePhone", "Contact")}
                />
              </div>
              <SelectInput
                source="contact_role"
                label={field(contactFields, "TaxId", "Contact")}
                choices={toChoices(CONTACT_ROLES)}
              />
              <BooleanInput
                source="contact_marketing_consent"
                label={field(contactFields, "Is1099Required", "Contact")}
              />
            </Section>

            <Toolbar
              canCreate={canCreate}
              onPreview={setPreview}
              createClient={(input) => dataProvider.createClient(input)}
            />
          </CardContent>
        </Card>
      </Form>
      {preview ? (
        <RequestPreview
          requests={preview}
          note="These routes are proposals: zInspector's public API does not have them yet. The first request returns the new property's id, which the next ones use."
        />
      ) : null}
    </div>
  );
};

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <section className="flex flex-col gap-4">
    <h2 className="text-lg font-semibold text-muted-foreground">{title}</h2>
    {children}
  </section>
);

interface ToolbarProps {
  canCreate: boolean;
  onPreview: (requests: ZiWriteRequest[]) => void;
  createClient: (input: NewClientInput) => Promise<{ id: number }>;
}

const Toolbar = ({ canCreate, onPreview, createClient }: ToolbarProps) => {
  const { handleSubmit } = useFormContext();
  const notify = useNotify();
  const redirect = useRedirect();
  const [saving, setSaving] = useState(false);

  const preview = handleSubmit((values) => {
    onPreview(buildNewClientRequests(values as NewClientInput));
  });

  const create = handleSubmit(async (values) => {
    setSaving(true);
    try {
      const { id } = await createClient(values as NewClientInput);
      notify("Account created", { type: "success" });
      redirect("show", "companies", id);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Creation failed", {
        type: "error",
      });
      setSaving(false);
    }
  });

  return (
    <div role="toolbar" className="flex flex-row justify-end gap-2">
      <CancelButton />
      <Button type="button" variant="outline" onClick={preview}>
        Preview the zInspector requests
      </Button>
      {canCreate ? (
        <Button type="button" onClick={create} disabled={saving}>
          Create account
        </Button>
      ) : null}
    </div>
  );
};
